/**
 * Recovery from chunks that fail to load (`vite:preloadError`, installed by main.tsx).
 *
 * Vite's preload helper dispatches the event (cancelable, the error in `payload`) for a
 * stylesheet that failed to load (`Unable to preload CSS for <url>`: e.g. a previewed style's
 * font file, offline) and for a JS chunk that failed (a stale chunk after a deploy). Unless a
 * listener calls preventDefault(), the import fails.
 *
 * - A stylesheet: preventDefault() and keep going without it. Text keeps the fallback font
 *   stack; nothing is worth a reload.
 * - A JS chunk: reload once so the page picks up the new build. The build that reloaded is kept
 *   in sessionStorage; if the same build fails again after its reload (the chunk is really
 *   unreachable, e.g. an offline kiosk) there is no second reload, so a kiosk cannot loop. A
 *   later deploy (another build) may reload once again. Without session storage the guard cannot
 *   be kept, so there is no reload.
 */

/** sessionStorage key: the build (entry module URL) that last reloaded after a failed chunk. */
export const PRELOAD_RELOAD_KEY = 'kiosk:preload-error-reload';

export interface PreloadErrorEvent extends Event {
  /** The error Vite's preload helper caught. */
  payload?: unknown;
}

type SessionStore = Pick<Storage, 'getItem' | 'setItem'>;

export interface PreloadErrorDeps {
  /** sessionStorage, or null when it is not available. */
  storage: SessionStore | null;
  reload: () => void;
  /** The running build: main.tsx passes its own module URL, which carries the build hash. */
  buildId: string;
}

export type PreloadErrorOutcome = 'kept-fallback' | 'reloaded' | 'not-reloaded';

function messageOf(payload: unknown): string {
  if (payload instanceof Error) return payload.message;
  return typeof payload === 'string' ? payload : '';
}

/** A stylesheet that failed (Vite: `Unable to preload CSS for <url>`), not a JS chunk. */
export function isCssPreloadError(payload: unknown): boolean {
  const message = messageOf(payload).trim();
  return /\bUnable to preload CSS\b/.test(message) || /\.css(?:[?#]\S*)?$/.test(message);
}

export function handlePreloadError(event: PreloadErrorEvent, { storage, reload, buildId }: PreloadErrorDeps): PreloadErrorOutcome {
  if (isCssPreloadError(event.payload)) {
    event.preventDefault();
    return 'kept-fallback';
  }
  if (!storage) return 'not-reloaded';
  try {
    if (storage.getItem(PRELOAD_RELOAD_KEY) === buildId) return 'not-reloaded';
    storage.setItem(PRELOAD_RELOAD_KEY, buildId);
  } catch {
    return 'not-reloaded';
  }
  reload();
  return 'reloaded';
}

export interface InstallOptions {
  /** Returns sessionStorage (read when an error arrives); null or a throw means none. */
  storage: () => SessionStore | null;
  reload: () => void;
  buildId: string;
}

/** Listens for `vite:preloadError` on `target`; returns a function that removes the listener. */
export function installPreloadErrorRecovery(target: Window, { storage, reload, buildId }: InstallOptions): () => void {
  const listener = (event: Event) => {
    let store: SessionStore | null = null;
    try {
      store = storage();
    } catch {
      store = null;
    }
    handlePreloadError(event as PreloadErrorEvent, { storage: store, reload, buildId });
  };
  target.addEventListener('vite:preloadError', listener);
  return () => target.removeEventListener('vite:preloadError', listener);
}
